import React from 'react';

/**
 * Highlights search terms in text with a yellow background
 * @param text - The text to search and highlight in
 * @param searchTerm - The term to highlight (case insensitive)
 * @param className - Optional CSS classes to apply to the highlighted text
 * @returns JSX element with highlighted text
 */
export const highlightText = (
  text: string, 
  searchTerm: string, 
  className: string = 'bg-yellow-200 dark:bg-yellow-800 dark:text-yellow-100 px-1 rounded'
): React.ReactNode => {
  if (!searchTerm || !searchTerm.trim()) {
    return text;
  }

  // Escape special regex characters in the search term
  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  
  // Create regex for case-insensitive global search
  const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
  
  // Split the text by the search term while preserving the delimiter
  const parts = text.split(regex);
  
  return (
    <>
      {parts.map((part, index) => {
        // Check if this part matches the search term (case insensitive)
        if (part.toLowerCase() === searchTerm.toLowerCase()) {
          return (
            <span key={index} className={className}>
              {part}
            </span>
          );
        }
        return part;
      })}
    </>
  );
};

/**
 * Checks if a text contains a search term (case insensitive)
 * @param text - The text to search in
 * @param searchTerm - The term to search for
 * @returns boolean indicating if the text contains the search term
 */
export const containsSearchTerm = (text: string, searchTerm: string): boolean => {
  if (!searchTerm || !searchTerm.trim()) {
    return false;
  }
  return text.toLowerCase().includes(searchTerm.toLowerCase());
};

/**
 * Highlights search terms in multiple fields of an object
 * Useful for showing why a contact/matter matched the search
 * @param fields - Array of field values to check
 * @param searchTerm - The term to highlight
 * @param className - Optional CSS classes for highlighting
 * @returns Array of highlighted field values
 */
export const highlightInFields = (
  fields: (string | undefined | null)[],
  searchTerm: string,
  className?: string
): React.ReactNode[] => {
  return fields.map((field) => {
    if (!field) return null;
    return highlightText(field, searchTerm, className);
  });
};
